// app/(main)/paths/[slug]/PathDetailClient.tsx
"use client";

import { useAuth } from "@/lib/AuthContext";
import { savePostLoginIntent, consumePostLoginIntent } from "@/lib/postLoginAction";
import GlobalBackButton from "@/components/shared/GlobalBackButton";
import DetailBreadcrumb, { type DetailBreadcrumbItem } from "@/components/seo/DetailBreadcrumb";
import { buildLandingUrl } from "@/lib/seo/slug";
import { useState, useTransition, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  Lock, Play, Trophy, Clock, BookOpen, Star, Users,
  Award, ChevronRight, ChevronDown, CheckCircle2, Loader2,
  Sparkles, ArrowLeft, DollarSign, User, Gem, Unlock, Eye,
  Target, XCircle, Brain,
} from "lucide-react";
// Dynamic import ở handler để tránh bug Next.js navigation stuck với static action import
import PathFullscreen from "@/components/study/PathFullscreen";
import { useDiamonds } from "@/lib/DiamondsContext";
import PurchaseConfirmModal from "@/components/shared/PurchaseConfirmModal";
import { useTopupReturn } from "@/hooks/useTopupReturn";

interface CategoryContext {
  domain:   { name: string; slug: string } | null;
  org:      { name: string; slug: string } | null;
  position: { name: string; slug: string } | null;
}

interface PathProgress {
  correctCount: number;
  wrongCount: number;
  totalAnswered: number;
  answeredIds: string[];
  lastAnsweredAt: string | null;
}

interface Props {
  path: any;
  topics: any[];
  badges: any[];
  enrollment: any | null;
  leaderboard: any[];
  similar: any[];
  progress: PathProgress | null;
  categoryContext?: CategoryContext;
  isOwner?: boolean;
}

// ── Số câu teaser (blur) cho user chưa mua ──
const TEASER_COUNT = 2;

export default function PathDetailClient({
  path, topics, badges, enrollment, leaderboard, similar, progress, categoryContext, isOwner = false,
}: Props) {
  const router = useRouter();
  const { user, requireAuth } = useAuth();
  const [isPending, startTransition] = useTransition();
  const [enrolling, setEnrolling] = useState(false);

  const gateAction = (actionId: string, runner: () => void) => {
    if (user) { runner(); return; }
    savePostLoginIntent(actionId);
    requireAuth();
  };
  const [completing, setCompleting] = useState(false);
  const [awardedBadges, setAwardedBadges] = useState<any[]>([]);
  const [showCelebration, setShowCelebration] = useState(false);
  const [studyOpen, setStudyOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { diamonds, addDiamonds } = useDiamonds();
  // Mặc định: TẤT CẢ collapsed
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());

  // Sau login (page reload) → consume intent → auto-fire
  useEffect(() => {
    if (!user) return;
    const intent = consumePostLoginIntent();
    if (!intent) return;
    if (intent.action === "start-path" || intent.action === "demo-path") {
      setStudyOpen(true);
    } else if (intent.action === "enroll-path") {
      const isFreeNow = path.price === 0;
      if (isFreeNow) handleEnroll();
      else setShowConfirm(true);
    }
  }, [user]);

  // Quay về từ app ngân hàng (?topup=) → tiền vào ví → mở confirm mua luôn
  useTopupReturn(!!user, () => setShowConfirm(true));

  // Link có ?coupon=CODE (mail upsell, chiến dịch) → đã login + chưa mua thì mở modal, mã tự áp dụng
  const [urlCoupon, setUrlCoupon] = useState<string | undefined>(undefined);
  useEffect(() => {
    const c = new URLSearchParams(window.location.search).get("coupon");
    if (!c) return;
    setUrlCoupon(c);
    if (user && !enrollment && !isOwner && (path.price ?? 0) > 0) setShowConfirm(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const isEnrolled = !!enrollment;
  const isFree = path.price === 0;
  const canStudy = isOwner || isEnrolled; // owner/admin bypass paywall
  const totalTopicQuestions = topics.reduce((sum: number, t: any) => sum + (t.path_items?.length ?? 0), 0);
  const demoCount = topics.reduce((sum: number, t: any) =>
    sum + (t.path_items ?? []).filter((i: any) => i.is_demo).length, 0);
  const creator = path.creator;

  // ── Progress stats (từ mindmap_question_progress) ──
  const answeredSet = new Set(progress?.answeredIds ?? []);
  const correctCount = progress?.correctCount ?? 0;
  const wrongCount = progress?.wrongCount ?? 0;
  const totalAnswered = progress?.totalAnswered ?? 0;
  const progressPct = totalTopicQuestions > 0 ? (totalAnswered / totalTopicQuestions) * 100 : 0;
  const displayPct = progressPct % 1 === 0 ? `${Math.round(progressPct)}` : progressPct.toFixed(1);
  const accuracyPct = totalAnswered > 0 ? Math.round((correctCount / totalAnswered) * 100) : 0;
  const errorPct = totalAnswered > 0 ? 100 - accuracyPct : 0;

  async function handleEnroll(couponCode?: string) {
    setEnrolling(true);
    try {
      const { enrollInPath } = await import("@/actions/paths");
      const res = await enrollInPath(path.id, couponCode);
      setEnrolling(false);
      if (!res.success) {
        alert(res.error ?? "Có lỗi xảy ra");
        return;
      }
      // Broadcast diamond change cho topbar countdown
      const paid = res.paid ?? path.price ?? 0;
      if (paid > 0 && !res.already) {
        addDiamonds(-paid, "buy_path");
      }
      router.refresh();
      setStudyOpen(true);
    } catch {
      setEnrolling(false);
      alert("Lỗi kết nối. Vui lòng thử lại.");
    }
  }

  async function handleComplete() {
    setCompleting(true);
    try {
      const { completeLearningPath } = await import("@/actions/paths");
      const res = await completeLearningPath(path.id);
      setCompleting(false);
      if (res.success && res.data) {
        setAwardedBadges(res.data.badges);
        if (res.data.badges.length > 0) setShowCelebration(true);
        router.refresh();
      }
    } catch {
      setCompleting(false);
      alert("Lỗi kết nối. Vui lòng thử lại.");
    }
  }

  function toggleTopic(id: string) {
    setExpandedTopics(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-3">

        {/* Breadcrumb */}
        <DetailBreadcrumb items={buildPathBreadcrumbs(path, categoryContext)} />

        {/* ══ HERO BANNER ══ */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-800 via-[#1a5570] to-teal-600 p-6 sm:p-8">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_40%,rgba(249,115,22,.16),transparent_55%)] pointer-events-none" />
          <div className="relative">
            {/* Top Tags */}
            <div className="flex items-center gap-2 flex-wrap mb-4">
              {path.org?.name && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold bg-teal-50/20 text-teal-200 border border-teal-400/30">🏦 {path.org.name}</span>
              )}
              {path.position?.name && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold bg-orange-500/20 text-orange-300 border border-orange-400/30">💼 {path.position.name}</span>
              )}
              {isEnrolled && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
                  <CheckCircle2 className="w-3 h-3" />Đã sở hữu
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-white leading-tight mb-2">
              {path.title}
            </h1>

            {/* Subtitle */}
            <p className="text-[13px] text-white/50 mb-5">
              by <span className="text-white/70 font-semibold">{creator?.full_name ?? creator?.username ?? "Tác giả"}</span>
              {" · "}{totalTopicQuestions} câu hỏi · {topics.length} phần · {path.student_count ?? 0} người đã mua
            </p>

            {/* CTA Buttons */}
            <div className="flex items-center gap-3 flex-wrap">
              {isOwner ? (
                <>
                  <button onClick={() => gateAction("start-path", () => setStudyOpen(true))}
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-teal-700 text-white text-[14px] font-bold hover:bg-teal-600 transition-all shadow-[0_3px_12px_rgba(15,118,110,.4)]">
                    <Play className="w-4.5 h-4.5" />{totalAnswered > 0 ? "Tiếp tục học" : "Vào học (Chủ)"}
                  </button>
                  <a href={`/studio/paths/${path.id}`}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/25 bg-white/10 text-white text-[13px] font-semibold hover:bg-white/20 transition-colors no-underline">
                    ✏️ Chỉnh sửa
                  </a>
                  {(path.price ?? 0) > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/10 border border-white/20 text-white/70 text-[11px] font-semibold">
                      <Gem className="w-3 h-3 text-amber-400" />Giá bán: {path.price} 💎
                    </span>
                  )}
                </>
              ) : isEnrolled ? (
                <button onClick={() => gateAction("start-path", () => setStudyOpen(true))}
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-orange-500 text-white text-[14px] font-bold hover:bg-orange-600 transition-all shadow-[0_3px_12px_rgba(249,115,22,.4)]">
                  <Play className="w-4.5 h-4.5" />{totalAnswered > 0 ? `Tiếp tục học (câu ${totalAnswered + 1})` : "Bắt đầu học"}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => gateAction("enroll-path", () => {
                      if (isFree) { handleEnroll(); return; }
                      setShowConfirm(true);
                    })}
                    disabled={enrolling}
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-orange-500 text-white text-[14px] font-bold hover:bg-orange-600 transition-all shadow-[0_3px_12px_rgba(249,115,22,.4)]"
                  >
                    {enrolling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gem className="w-4 h-4" />}
                    {isFree ? "Bắt đầu học miễn phí" : `Mở khóa • ${path.price} 💎`}
                  </button>
                  <button onClick={() => setStudyOpen(true)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/25 bg-white/10 text-white text-[13px] font-semibold hover:bg-white/20 transition-colors">
                    <Unlock className="w-4 h-4" />Làm thử (Demo)
                  </button>
                </>
              )}
              {isEnrolled && progressPct >= 95 && (
                <button onClick={() => gateAction("complete-path", () => handleComplete())} disabled={completing}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-500 text-white text-[13px] font-bold hover:bg-amber-400 transition-all">
                  {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
                  Hoàn thành & Nhận huy hiệu
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ══ STATS ROW ══ */}
        {isEnrolled && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Card 1: Tiến độ */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Tiến độ</span>
                <BookOpen className="w-4 h-4 text-teal-500" />
              </div>
              <div className="text-[36px] font-extrabold text-teal-800 leading-none mb-3">{displayPct}%</div>
              <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden mb-2">
                <div className="h-full bg-gradient-to-r from-teal-600 to-teal-400 rounded-full transition-all duration-500" style={{ width: `${Math.min(progressPct, 100)}%` }} />
              </div>
              <p className="text-[11px] text-muted-foreground">Đã làm: {totalAnswered}/{totalTopicQuestions} câu</p>
              <p className="text-[9px] text-muted-foreground/60 mt-0.5 italic">* Tính từ Mindmap bản đồ tri thức</p>
            </div>

            {/* Card 2: Tổng quan */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Tổng quan</span>
                <Brain className="w-4 h-4 text-violet-500" />
              </div>
              <div className="flex items-end gap-4 mb-3">
                <div>
                  <p className="text-[10px] text-muted-foreground mb-0.5">Đã trả lời</p>
                  <p className="text-[28px] font-extrabold text-teal-800 leading-none">{totalAnswered}</p>
                </div>
                <div className="text-muted-foreground/20 text-lg font-light pb-1">/</div>
                <div>
                  <p className="text-[10px] text-muted-foreground mb-0.5">Tổng câu</p>
                  <p className="text-[28px] font-extrabold text-slate-400 leading-none">{totalTopicQuestions}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-[11px]">
                <span className="flex items-center gap-1 text-emerald-600 font-semibold">
                  <CheckCircle2 className="w-3 h-3" />{correctCount} đúng
                </span>
                <span className="flex items-center gap-1 text-red-500 font-semibold">
                  <XCircle className="w-3 h-3" />{wrongCount} sai
                </span>
              </div>
              <p className="text-[9px] text-muted-foreground/60 mt-1.5 italic">* Tính từ Mindmap bản đồ tri thức</p>
            </div>

            {/* Card 3: Hiệu suất — Dual bar */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Hiệu suất</span>
                <Target className="w-4 h-4 text-emerald-500" />
              </div>
              {totalAnswered > 0 ? (
                <>
                  <div className="flex items-end gap-4 mb-3">
                    <div>
                      <p className="text-[10px] text-muted-foreground mb-0.5">Tỷ lệ đúng</p>
                      <p className="text-[28px] font-extrabold text-emerald-600 leading-none">{accuracyPct}%</p>
                    </div>
                    <div className="text-muted-foreground/20 text-lg font-light pb-1">/</div>
                    <div>
                      <p className="text-[10px] text-muted-foreground mb-0.5">Tỷ lệ sai</p>
                      <p className="text-[28px] font-extrabold text-red-400 leading-none">{errorPct}%</p>
                    </div>
                  </div>
                  {/* Dual-color bar */}
                  <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden flex">
                    <div className="h-full bg-emerald-500 transition-all duration-500 rounded-l-full" style={{ width: `${accuracyPct}%` }} />
                    <div className="h-full bg-red-300 transition-all duration-500 rounded-r-full" style={{ width: `${errorPct}%` }} />
                  </div>
                  <p className="text-[9px] text-muted-foreground/60 mt-1.5 italic">* Tính trên {totalAnswered} câu đã trả lời</p>
                </>
              ) : (
                <div className="py-4 text-center">
                  <p className="text-[12px] text-muted-foreground">Chưa có dữ liệu</p>
                  <p className="text-[10px] text-muted-foreground/60 mt-1">Bắt đầu làm bài để xem hiệu suất</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ BODY: 2 COLUMNS ══ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ── LEFT: Mục lục lộ trình ── */}
          <div className="lg:col-span-2 space-y-3">
            <h2 className="text-[16px] font-extrabold text-teal-800 flex items-center gap-2 mb-1">
              📖 Mục lục lộ trình
              <span className="text-[11px] font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{topics.length} chủ đề</span>
            </h2>
            {topics.map((topic: any, idx: number) => {
              const isOpen = expandedTopics.has(topic.id);
              const topicItems: any[] = topic.path_items ?? [];
              const topicTotal = topicItems.length;

              // Đếm câu đã làm trong topic từ mindmap progress
              const topicAnswered = topicItems.filter((i: any) => answeredSet.has(i.item_id)).length;
              const topicCorrect = topicItems.filter((i: any) => {
                if (!answeredSet.has(i.item_id)) return false;
                return progress?.answeredIds?.includes(i.item_id) &&
                  (progress as any)?.correctCount > 0; // simplified
              }).length;
              const topicPct = topicTotal > 0 ? Math.round((topicAnswered / topicTotal) * 100) : 0;
              const isComplete = topicPct >= 100;
              const demoItems = topicItems.filter((i: any) => i.is_demo);
              const demoItemIds = new Set(demoItems.map((i: any) => i.id));

              // ── Zero-trust: phân loại items ──
              let visibleItems: any[] = [];
              let teaserItems: any[] = [];
              let hiddenCount = 0;

              if (isEnrolled) {
                // Đã mua → hiện tất cả
                visibleItems = topicItems;
              } else {
                // Chưa mua → demo + teaser + hidden
                const demos = topicItems.filter((i: any) => i.is_demo);
                const locked = topicItems.filter((i: any) => !i.is_demo);
                visibleItems = demos;
                teaserItems = locked.slice(0, TEASER_COUNT);
                hiddenCount = Math.max(0, locked.length - TEASER_COUNT);
              }

              // SVG ring
              const ringSize = 40;
              const sw = 3;
              const r = (ringSize - sw) / 2;
              const circ = 2 * Math.PI * r;
              const offset = circ - (topicPct / 100) * circ;

              return (
                <div key={topic.id} className={`rounded-2xl border overflow-hidden transition-all ${
                  isComplete ? "border-emerald-200 bg-emerald-50/30"
                  : isOpen ? "border-teal-200 bg-teal-50/10"
                  : "border-slate-200 bg-white"
                }`}>
                  {/* ── Accordion Header ── */}
                  <button onClick={() => toggleTopic(topic.id)}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-muted/20 transition-colors">
                    <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200 ${isOpen ? "" : "-rotate-90"}`} />
                    <span className="text-lg shrink-0">{topic.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-bold text-teal-800 truncate">{topic.title}</p>
                      <p className="text-[10px] text-muted-foreground">
                        Đã làm: {topicAnswered}/{topicTotal} câu
                        {!isEnrolled && demoItems.length > 0 && ` · ${demoItems.length} câu demo`}
                      </p>
                    </div>

                    {/* SVG Progress Ring */}
                    <div className="relative shrink-0" style={{ width: ringSize, height: ringSize }}>
                      <svg width={ringSize} height={ringSize} className="-rotate-90">
                        <circle cx={ringSize / 2} cy={ringSize / 2} r={r} fill="transparent" stroke="currentColor" strokeWidth={sw} className="text-slate-100" />
                        <circle cx={ringSize / 2} cy={ringSize / 2} r={r} fill="transparent" stroke="currentColor" strokeWidth={sw}
                          className={`${isComplete ? "text-emerald-500" : "text-teal-500"} transition-all duration-500`}
                          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-[9px] font-semibold text-teal-700">{topicPct}%</span>
                    </div>
                  </button>

                  {/* ── Accordion Body ── */}
                  {isOpen && (
                    <div className="px-4 pb-3 space-y-1.5 border-t border-slate-100">
                      {/* Visible items (demo hoặc tất cả nếu enrolled) */}
                      {visibleItems.map((item: any) => {
                        const itemDone = answeredSet.has(item.item_id);
                        return (
                          <div key={item.id} className={`flex items-center gap-2.5 p-2.5 rounded-xl border transition-all ${
                            item.is_demo && !isEnrolled ? "bg-emerald-50/50 border-emerald-200"
                            : itemDone ? "bg-emerald-50/30 border-emerald-100"
                            : "bg-[#f8fafc] border-[#f1f5f9]"
                          }`}>
                            {itemDone ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                            ) : isEnrolled ? (
                              <span className="w-4 h-4 rounded-full border-2 border-slate-200 shrink-0" />
                            ) : (
                              item.is_demo
                                ? <Eye className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                : <Lock className="w-3.5 h-3.5 text-muted-foreground/30 shrink-0" />
                            )}

                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${
                              item.item_type === "exam" ? "bg-blue-50 text-blue-700 border-blue-200"
                              : item.item_type === "theory" ? "bg-violet-50 text-violet-700 border-violet-200"
                              : "bg-teal-50 text-teal-700 border-teal-200"
                            }`}>
                              {item.item_type === "exam" ? "Đề thi" : item.item_type === "theory" ? "Lý thuyết" : "Câu hỏi"}
                            </span>
                            <p className="flex-1 text-[11px] text-[#334155] truncate">{item.title || "Bài học"}</p>
                            {item.is_demo && !isEnrolled && (
                              <span className="text-[8px] font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded-full shrink-0">DEMO</span>
                            )}
                          </div>
                        );
                      })}

                      {/* Teaser items (blur) — chỉ khi chưa mua */}
                      {!isEnrolled && teaserItems.map((item: any) => (
                        <div key={item.id} className="relative flex items-center gap-2.5 p-2.5 rounded-xl border border-slate-200 bg-slate-50/50 select-none pointer-events-none">
                          <Lock className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border bg-slate-100 text-slate-400 border-slate-200`}>
                            {item.item_type === "exam" ? "Đề thi" : "Câu hỏi"}
                          </span>
                          <p className="flex-1 text-[11px] text-[#334155] truncate blur-[5px]">{item.title || "Nội dung bài học VIP"}</p>
                          <Lock className="w-3 h-3 text-orange-400 shrink-0" />
                        </div>
                      ))}

                      {/* Paywall block — chỉ khi chưa mua và còn câu ẩn */}
                      {!isEnrolled && hiddenCount > 0 && (
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 mt-1">
                          <Lock className="w-4 h-4 text-orange-500 shrink-0" />
                          <div className="flex-1">
                            <p className="text-[11px] font-bold text-orange-700">
                              Còn {hiddenCount} câu hỏi VIP
                            </p>
                            <p className="text-[9px] text-orange-500">Mua để mở khóa toàn bộ nội dung</p>
                          </div>
                          <button
                            onClick={() => gateAction("enroll-path", () => {
                              if (isFree) { handleEnroll(); return; }
                              setShowConfirm(true);
                            })}
                            disabled={enrolling}
                            className="px-3 py-1.5 rounded-full bg-orange-500 text-white text-[10px] font-bold hover:bg-orange-600 transition-colors shrink-0"
                          >
                            {isFree ? "Mở khóa" : `${path.price} 💎`}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── RIGHT: Sidebar ── */}
          <div className="space-y-4">
            {/* Huy hiệu mục tiêu */}
            {badges.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="text-[13px] font-extrabold text-teal-800 flex items-center gap-1.5 mb-3">
                  <Award className="w-4 h-4 text-amber-500" />Huy hiệu sẽ đạt được
                </h3>
                <div className="space-y-2.5">
                  {badges.map((b: any) => (
                    <div key={b.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                      isEnrolled ? "border-amber-200 bg-amber-50/30" : "border-slate-100 bg-slate-50/50 opacity-70"
                    }`}>
                      <span className="text-2xl">{b.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-bold text-teal-800">{b.name}</p>
                        <p className="text-[10px] text-muted-foreground">{b.description}</p>
                        {b.diamond_reward > 0 && (
                          <p className="text-[9px] font-bold text-orange-500 mt-0.5">+{b.diamond_reward} 💎</p>
                        )}
                      </div>
                      {!isEnrolled && <Lock className="w-3.5 h-3.5 text-muted-foreground/30 shrink-0" />}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bảng xếp hạng */}
            {leaderboard.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="text-[13px] font-extrabold text-teal-800 flex items-center gap-1.5 mb-3">
                  <Trophy className="w-4 h-4 text-amber-500" />Bảng xếp hạng
                </h3>
                <div className="space-y-2">
                  {leaderboard.map((entry: any, i: number) => {
                    const u = entry.user;
                    const medals = ["🥇", "🥈", "🥉"];
                    return (
                      <div key={entry.user_id} className={`flex items-center gap-2.5 p-2.5 rounded-xl ${i < 3 ? "bg-amber-50/50" : ""}`}>
                        <span className="text-sm w-6 text-center">{i < 3 ? medals[i] : `${i + 1}`}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-semibold text-teal-800 truncate">{u?.full_name ?? u?.username ?? "Ẩn danh"}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-[11px] font-bold text-teal-700">{Math.round(entry.progress_percent)}%</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lộ trình tương tự */}
            {similar.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="text-[13px] font-extrabold text-teal-800 flex items-center gap-1.5 mb-3">
                  <Sparkles className="w-4 h-4 text-violet-500" />Lộ trình tương tự
                </h3>
                <div className="space-y-2.5">
                  {similar.map((p: any) => (
                    <Link key={p.id} href={`/paths/${p.slug || p.id}`}
                      className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-teal-300 hover:bg-teal-50/30 transition-all">
                      <span className="text-lg">{p.emoji || "📚"}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-bold text-teal-800 truncate">{p.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-muted-foreground">{p.total_questions} câu</span>
                          <span className="text-[10px] text-muted-foreground">{p.student_count ?? 0} người</span>
                          {p.price === 0 ? (
                            <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">Free</span>
                          ) : (
                            <span className="text-[9px] font-bold text-orange-600">{p.price} 💎</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/30 shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Purchase Confirm Modal */}
      <PurchaseConfirmModal
        open={showConfirm}
        type="path"
        title={path.title}
        price={path.price ?? 0}
        hasDemo={demoCount > 0}
        userBalance={diamonds}
        onConfirm={async (couponCode?: string) => {
          setShowConfirm(false);
          await handleEnroll(couponCode);
        }}
        onViewDemo={() => setStudyOpen(true)}
        onClose={() => setShowConfirm(false)}
        itemId={path.id}
        initialCoupon={urlCoupon}
      />

      {/* Study Fullscreen */}
      {studyOpen && <PathFullscreen pathId={path.id} onExit={() => { setStudyOpen(false); router.refresh(); }} />}

      {/* Celebration overlay */}
      {showCelebration && awardedBadges.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowCelebration(false)} />
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center space-y-4 animate-fade-slide">
            <div className="text-5xl">🎉</div>
            <h2 className="text-[20px] font-extrabold text-teal-800">Chúc mừng!</h2>
            <p className="text-[14px] text-muted-foreground">Bạn đã nhận được {awardedBadges.length} huy hiệu mới</p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              {awardedBadges.map((b: any) => (
                <div key={b.id} className="flex flex-col items-center gap-1 p-3 rounded-xl bg-amber-50 border border-amber-200">
                  <span className="text-3xl">{b.emoji}</span>
                  <p className="text-[11px] font-bold text-amber-700">{b.name}</p>
                </div>
              ))}
            </div>
            <button onClick={() => setShowCelebration(false)}
              className="px-6 py-2.5 rounded-full bg-teal-800 text-white text-[13px] font-bold hover:bg-teal-700 transition-colors">
              Tuyệt vời!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// BREADCRUMB BUILDER
// ═══════════════════════════════════════════════════════════════
function buildPathBreadcrumbs(
  path: any,
  ctx?: { domain: any; org: any; position: any }
): DetailBreadcrumbItem[] {
  const items: DetailBreadcrumbItem[] = [
    { label: "Lộ trình", href: "/paths" },
  ];

  if (ctx?.domain) {
    items.push({
      label: ctx.domain.name,
      href: buildLandingUrl("paths", { domain: ctx.domain.slug }),
    });
  }

  if (ctx?.org && ctx.domain) {
    items.push({
      label: ctx.org.name,
      href: buildLandingUrl("paths", {
        domain: ctx.domain.slug,
        org: ctx.org.slug,
      }),
    });
  }

  if (ctx?.position && ctx.domain && ctx.org) {
    items.push({
      label: ctx.position.name,
      href: buildLandingUrl("paths", {
        domain: ctx.domain.slug,
        org: ctx.org.slug,
        position: ctx.position.slug,
      }),
    });
  }

  items.push({ label: path.title ?? "Lộ trình" });

  return items;
}
